import React from 'react';
import {Grid, Row, Col} from '@/grid';
import Link from '@/links';
import {useTranslations} from 'next-intl';
import {PSmallMarginSm, P} from '@/typography';
import {BreakVisibleXs} from '@/ui/breaks';
import {withRowStretch, withFlexHolderSpaceBetween} from '@/hocs/withFlex';
import AppBadges from '@/ui/app-badges';
import Container from '@/container';
import {withMarginBottomMd} from '@/hocs/withSpacing';
import cx from 'classnames';
import Social from './social';
import styles from './footer.module.scss';

const ListSpaceBetween = withFlexHolderSpaceBetween('ul');
const RowStretchRow = withRowStretch(Row);
const BottomMarginedMdP = withMarginBottomMd(P);

/* istanbul ignore next */
const renderLinksList = ({title, items}) => (
  <>
    <BottomMarginedMdP className="heading-5">{title}</BottomMarginedMdP>
    <ul>
      {items.map(({title: linkTitle, href, target, internal, variant = 'highlighted'}, index) => (
        <li key={`footer-link-${index}`}>
          <Link href={href} target={target} internal={internal} variant={variant}>
            {linkTitle}
          </Link>
        </li>
      ))}
    </ul>
  </>
);

export const DefaultFooter = () => {
  const productT = useTranslations('shared.footer.product');
  const resourcesT = useTranslations('shared.footer.resources');
  const workableT = useTranslations('shared.footer.workable');
  const contactT = useTranslations('shared.footer.contact');

  const productLinks = {
    title: productT('title'),
    items: [
      {
        title: productT('pricing'),
        href: '/pricing',
        target: '_self'
      },
      {
        title: productT('features'),
        href: '/features',
        target: '_self'
      },
      {
        title: productT('aiTechnology'),
        href: '/workable-ai',
        target: '_self'
      },
      {
        title: productT('partners'),
        href: 'https://partners.workable.com',
        target: '_blank'
      },
      {
        title: productT('security'),
        href: '/security',
        target: '_self'
      }
    ]
  };

  const employeeResourcesLinks = {
    title: resourcesT('title'),
    items: [
      {
        title: resourcesT('helpCenter'),
        href: 'https://help.workable.com',
        target: '_blank',
        internal: true
      },
      {
        title: resourcesT('resourceLibrary'),
        href: 'https://resources.workable.com',
        target: '_blank',
        internal: true
      },
      {
        title: resourcesT('developerAPI'),
        href: '/developers',
        target: '_self'
      },
      {
        title: resourcesT('searchForJobs'),
        href: 'https://jobs.workable.com/',
        target: '_blank',
        internal: true
      }
    ]
  };

  const workableLinks = {
    title: workableT('title'),
    items: [
      {
        title: workableT('aboutUs'),
        href: '/about',
        target: '_self'
      },
      {
        title: workableT('workWithUs'),
        href: 'https://apply.workable.com/careers',
        target: '_self',
        internal: true
      },
      {
        title: workableT('backstage'),
        href: 'https://resources.workable.com/backstage-at-workable/',
        target: '_blank',
        internal: true
      },
      {
        title: workableT('accessibility'),
        href: '/web-accessibility-statement',
        target: '_self'
      },
      {
        title: workableT('status'),
        href: 'https://workable.statuspage.io/',
        target: '_blank',
        internal: true
      }
    ]
  };

  return (
    <div className={cx(styles['footer-upper'], 'no-print')}>
      <div className={styles['footer-menu']}>
        <Container>
          <Grid fluid>
            <RowStretchRow top="xs">
              <Col xs={12} md={6}>
                <ListSpaceBetween className={styles['footer-list']}>
                  <li>{renderLinksList(productLinks)}</li>
                  <li>{renderLinksList(employeeResourcesLinks)}</li>
                  <li>{renderLinksList(workableLinks)}</li>
                </ListSpaceBetween>
              </Col>
              <Col xs={12} md={5} mdOffset={1}>
                <div className={styles['grey-contact']}>
                  <PSmallMarginSm>
                    {contactT('americas')}{' '}
                    <Link
                      href={`tel:${process.env.NEXT_PUBLIC_PHONE_NUMBER_ROW.trim().replace(/\s+/g, '')}`}
                      variant="highlighted">
                      {process.env.NEXT_PUBLIC_PHONE_NUMBER_ROW}
                    </Link>
                  </PSmallMarginSm>
                  <PSmallMarginSm>
                    {contactT('europeAndRest')}
                    <BreakVisibleXs />
                    <Link
                      href={`tel:${process.env.NEXT_PUBLIC_PHONE_NUMBER_UK.trim().replace(/\s+/g, '')}`}
                      variant="highlighted">
                      {process.env.NEXT_PUBLIC_PHONE_NUMBER_UK}
                    </Link>
                  </PSmallMarginSm>
                  <Social variant="dark" />

                  <AppBadges variant="neutral900" />
                </div>
              </Col>
            </RowStretchRow>
          </Grid>
        </Container>
      </div>
    </div>
  );
};
